import { useRouter } from 'next/router'
import { useMemo } from 'react'

import { useUser } from 'src/providers/UserProvider'

import { ButtonLinkRebrand } from 'src/components/atoms/ButtonRebrand'
import { LoadingSpinner } from 'src/components/atoms/LoadingSpinner'
import { Paragraph } from 'src/components/atoms/Typography'
import { MonitoringsTable } from 'src/components/organisms/tables/MonitoringsTable'
import { VStack } from 'src/components/templates/VStack'

import {
  useGetApplicationQuery,
  useGetMonitoringsQuery
} from 'src/config/generated/graphql'
import { createApplicationMonitoringPath } from 'src/config/paths'
import { NotFound } from 'src/pages/404/404'
import { ApplicationWorkflowState } from 'src/types/application'
import { ListMonitoringType } from 'src/types/monitoring'
import { PlantingType, PlantingWorkflowState } from 'src/types/planting'

import { PlantingNav, PlantingNavTab } from '../navs/PlantingNav'

export const MonitoringReport = () => {
  const router = useRouter()
  const { user } = useUser()

  const { applicationId, plantingId, projectId } = router.query as {
    projectId: string
    applicationId: string
    plantingId: string
  }

  const { data: monitoringData, loading } = useGetMonitoringsQuery({
    fetchPolicy: 'network-only',
    variables: { plantingId }
  })

  const {
    called: applicationCalled,
    data: applicationData,
    loading: applicationLoading
  } = useGetApplicationQuery({
    fetchPolicy: 'network-only',
    variables: { id: applicationId, planterId: user?.Account?.PlanterId }
  })

  const application = useMemo(
    () => applicationData?.Application?.[0],
    [applicationData]
  )

  const plantings = useMemo(() => application?.Plantings || [], [application])
  const planting = monitoringData?.Planting_by_pk as PlantingType

  const numberOfTreesReported = useMemo(
    () =>
      plantings
        .filter(p => p.WorkflowState !== PlantingWorkflowState.Draft)
        .reduce((acc, curr) => acc + curr.Quantity, 0),
    [plantings]
  )

  const isFullyPlanted = numberOfTreesReported >= (application?.Approved ?? 0)
  const hasMonitorings = !!monitoringData?.MonitoringEvents?.length

  if (!loading && !monitoringData) return <NotFound />
  if (loading) return null

  if (applicationCalled && !applicationLoading && !application)
    return <NotFound />
  return (
    <VStack>
      <LoadingSpinner isLoading={loading}>
        <PlantingNav activeTab={PlantingNavTab.Monitorings} planting={planting}>
          {hasMonitorings &&
          (isFullyPlanted ||
            application?.WorkflowState ===
              ApplicationWorkflowState.Monitoring) ? (
            <>
              <MonitoringsTable
                isAdmin={false}
                plantingId={plantingId}
                plantingQuantity={planting.Quantity}
                applicationId={applicationId}
                monitoringsData={
                  (monitoringData?.MonitoringEvents as ListMonitoringType[]) ||
                  []
                }
                projectId={projectId}
              />
              <ButtonLinkRebrand
                className='mt-6 self-start'
                href={createApplicationMonitoringPath({
                  routeParams: { applicationId, plantingId, projectId }
                })}
              >
                Add monitoring
              </ButtonLinkRebrand>
            </>
          ) : null}

          {isFullyPlanted && !hasMonitorings && (
            <div className='px-4'>
              <Paragraph>
                Looks like you haven&apos;t added any monitorings for your
                planting yet.
              </Paragraph>
              <Paragraph>Please do this on an annual basis.</Paragraph>
              {planting.WorkflowState === PlantingWorkflowState.Submitted ? (
                <ButtonLinkRebrand
                  href={createApplicationMonitoringPath({
                    routeParams: { applicationId, plantingId, projectId }
                  })}
                >
                  Start
                </ButtonLinkRebrand>
              ) : (
                <Paragraph>
                  Submit your planting report before monitoring.
                </Paragraph>
              )}
            </div>
          )}

          {!hasMonitorings && !isFullyPlanted && (
            <Paragraph>
              You must report all of your planted trees before you can begin
              monitoring.
            </Paragraph>
          )}
        </PlantingNav>
      </LoadingSpinner>
    </VStack>
  )
}
